import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Trash } from '@phosphor-icons/react'
import { useNavigate, useParams } from 'react-router-dom'

import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { toastFactory } from '@/factories'
import { useGetPendingComments } from '@/api/groups/get-pending-comments'

import { convertUrlToFile } from '../GroupsSettings/AddNewGroupPopup/helpers'
import ImageInput from '../../EventManagement/common/components/formInputs/ImageInput'
import Dashboard from '../../Dashboard'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import { selectGroupById } from '../../../reducers/Organisation/groupsReducer'
import api from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { getAssetLink } from '../../../helpers/get-asset-link'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import NeighborsWindow from '../GroupsSettings/NeighborsWindow/NeighborsWindow'
import FilesInput from '../../EventManagement/common/components/formInputs/FilesInput'

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Group name is required')
    .max(50, 'Group name must be less than 50 characters'),
  description: Yup.string()
    .required('Description is required')
    .max(1000, 'Description must be less than 1000 characters'),
  image: Yup.mixed().required('Cover image is required'),
})

const GroupForm = () => {
  const { groupId } = useParams()
  const { displaySnackbar } = useSnackbar()

  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [groupData, setGroupData] = useState(useSelector(selectGroupById)(groupId ?? 'new'))
  const [deleteGroup, setDeleteGroup] = useState(null)
  const [isVisibleAllNeighbors, setVisibleAllNeighbors] = useState(false)
  const isGroupDeletable = !groupData?.system

  const { data } = useGetPendingComments({
    buildingId: groupData?.buildingId,
    skip: 0,
    limit: DEFAULT_LIMIT,
    groupId: groupData?.id,
  })

  const isEditMode = !!groupId
  const members = groupData?.members
  const groupHasPendingComments = data?.count > 0

  const navigate = useNavigate()

  const building = useSelector(selectActiveBuilding)

  const initialValues = useMemo(
    () => ({
      name: groupData && groupData.name ? groupData.name : '',
      description: groupData && groupData.description ? groupData.description : '',
      image: groupData && groupData.image ? groupData.image : null,
      active: groupData && groupData.active !== undefined ? groupData.active : true,
      showResidentsAvatar: !(groupData?.hideResidentsAvatar ?? true),
      enableWelcomePost: !(groupData?.disableWelcomePost ?? true),
      allowComments: !(groupData?.disableComments ?? false),
      guidelines: groupData?.guidelines ? [groupData?.guidelines] : [],
      approveComments: groupData?.approveComments ?? false,
    }),
    [groupData],
  )

  const validateFormFields = async (validateForm, setErrors, setFieldTouched) => {
    setSubmitted(true)
    const validationErrors = await validateForm()

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      Object.keys(validationErrors).forEach((field) => setFieldTouched(field, true, false))
      displaySnackbar('Please complete all required fields before continuing.', true)
      return false
    }

    setErrors({})
    return true
  }

  const clearFieldError = async (fieldName, fieldValue, errors, setErrors, values) => {
    if (!submitted || !errors[fieldName]) return
    try {
      await validationSchema.validateAt(fieldName, { ...values, [fieldName]: fieldValue })
      const { [fieldName]: _removed, ...rest } = errors
      setErrors(rest)
    } catch {
      // keep existing error
    }
  }

  const handleFieldChange = async (event, setFieldValue, errors, setErrors, values) => {
    const { name, value } = event.target
    setFieldValue(name, value)
    await clearFieldError(name, value, errors, setErrors, { ...values, [name]: value })
  }

  const handleImageChange = async (name, value, setFieldValue, errors, setErrors, values) => {
    setFieldValue(name, value)
    await clearFieldError(name, value, errors, setErrors, { ...values, [name]: value })
  }

  const handleSubmit = async (validateForm, values, setErrors, setFieldTouched, setSubmitting) => {
    if (!building) return

    const isValid = await validateFormFields(validateForm, setErrors, setFieldTouched)
    if (!isValid) {
      setSubmitting(false)
      return
    }

    setSubmitting(true)
    setLoading(true)

    try {
      const formData = {
        name: values.name,
        description: values.description,
        active: values.active,
        hideResidentsAvatar: !values.showResidentsAvatar,
        disableWelcomePost: !values.enableWelcomePost,
        disableComments: !values.allowComments,
        approveComments: values.approveComments,
      }

      if (values.guidelines && values.guidelines[0] instanceof File) {
        formData.guidelines = values.guidelines
      } else if (values.guidelines.length === 0) {
        formData.guidelines = null
      }

      if (values.image instanceof File) {
        formData.image = values.image
      } else if (values.image?.url) {
        formData.image = await convertUrlToFile(values.image.url)
      }

      if (isEditMode) {
        await api.updateGroup(formData, groupId)
        await fetchGroup()
        displaySnackbar('Group updated successfully')
      } else {
        const { data } = await api.createGroup(building.id, formData)
        const groupId = data.id
        navigate(`/dashboard/building-dashboard/groups-settings/edit/${groupId}`)
        displaySnackbar('Group created successfully')
      }
    } catch {
      displaySnackbar(isEditMode ? 'Failed to update group' : 'Failed to create group', true)
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  const handleDeleteGroup = async () => {
    setLoading(true)
    try {
      await api.deleteGroup(deleteGroup.id)
      displaySnackbar('Group deleted successfully')
      navigate('/dashboard/building-dashboard/groups-settings')
    } catch {
      displaySnackbar('Failed to delete group', true)
    } finally {
      setLoading(false)
    }
  }

  const fetchGroup = async () => {
    try {
      setLoading(true)
      const { data } = await api.getGroupById(groupId)
      setGroupData(data)
    } catch {
      displaySnackbar('Failed to fetch group', true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isEditMode && !groupData) {
      fetchGroup()
    }
  }, [isEditMode, groupData])

  return (
    <>
      <Dashboard.Container className="ctp-group-form">
        <div className="group-form-content">
          <div className="form-section">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              enableReinitialize
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={() => {}}
            >
              {({
                values,
                errors,
                touched,
                setFieldValue,
                isSubmitting,
                validateForm,
                setErrors,
                setFieldTouched,
                setSubmitting,
              }) => {
                return (
                  <form className="group-form" onSubmit={(e) => e.preventDefault()}>
                    {/* General Information Section */}
                    <div className="form-section">
                      <h2 className="section-title">General Information</h2>

                      <div className="form-row">
                        <div className="form-field">
                          <ToggleField
                            label="Visible in App"
                            name="active"
                            value={values.active}
                            onChange={(e) => setFieldValue('active', e.target.checked)}
                            labelOnRight
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-field">
                          <TextField
                            label="Group Name *"
                            name="name"
                            placeholder="Enter Group Name"
                            value={values.name}
                            maxLength={50}
                            onChange={(e) =>
                              handleFieldChange(e, setFieldValue, errors, setErrors, values)
                            }
                            isError={touched.name && !!errors.name}
                          />
                        </div>
                        {errors.name && <span className="form-step__error">{errors.name}</span>}
                      </div>

                      <div className="form-row upload-cover-image">
                        <div className="form-field input-row">
                          <div className="input-container">
                            <ImageInput
                              columnSize={12}
                              name="image"
                              value={values.image}
                              eventName={values.name}
                              eventType="Group"
                              setFieldValue={(fieldName, fieldValue) =>
                                handleImageChange(
                                  fieldName,
                                  fieldValue,
                                  setFieldValue,
                                  errors,
                                  setErrors,
                                  values,
                                )
                              }
                              isError={!!errors.image}
                              assetPath="chitown-images/groups"
                            />
                            {errors.image && (
                              <span className="form-step__error">{errors.image}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-row guidline-file-input">
                        <FilesInput
                          columnsSize={12}
                          label="Guidelines"
                          files={values.guidelines}
                          handleFieldChange={(e) => setFieldValue('guidelines', e.target.value)}
                          accept={['pdf']}
                          maxNumberOfFiles={1}
                          maxFileSizeInMB={10}
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-field">
                          <TextAreaField
                            label="Description *"
                            name="description"
                            placeholder="Provide group description..."
                            value={values.description}
                            onChange={(e) =>
                              handleFieldChange(e, setFieldValue, errors, setErrors, values)
                            }
                            maxLength={1000}
                            rows={4}
                            isError={touched.description && !!errors.description}
                          />
                          {errors.description ? (
                            <span className="form-step__error">{errors.description}</span>
                          ) : (
                            <div className="character-count">
                              Maximum input length is 1000 characters.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Neighbours Section */}
                    <div className="form-section">
                      <div className="divider" />
                      <div className="form-section-columns">
                        <div className="left-section">
                          <h2 className="section-title">Neighbours</h2>
                          <p className="section-description">
                            Show resident profile photos in Neighbors section
                          </p>
                        </div>
                        <div className="right-section">
                          <div className="form-row">
                            <div className="form-field">
                              <ToggleField
                                name="showResidentsAvatar"
                                value={values.showResidentsAvatar}
                                onChange={(e) =>
                                  setFieldValue('showResidentsAvatar', e.target.checked)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="neighbors-preview"
                        onClick={() => setVisibleAllNeighbors(true)}
                      >
                        {members &&
                          members?.avatars?.slice(0, 7).map((memberAvatar, i) => (
                            <div key={`${memberAvatar}_${i}`} className="neighbor-avatar">
                              <img
                                src={getAssetLink({
                                  path: 'chitown-avatar/avatar',
                                  file: memberAvatar,
                                })}
                              />
                            </div>
                          ))}
                        {members && members?.avatars?.length > 7 ? (
                          <div className="neighbor-count">{`+${members?.count - 7}`}</div>
                        ) : null}
                      </div>
                      <div className="divider" />
                    </div>
                    {/* Join Post Section */}
                    <div className="form-section">
                      <div className="form-section-columns">
                        <div className="left-section">
                          <h2 className="section-title">Join Post</h2>
                          <p className="section-description">
                            Trigger group post and notifications when someone joins
                          </p>
                        </div>

                        <div className="right-section">
                          <div className="form-row">
                            <div className="form-field">
                              <ToggleField
                                name="enableWelcomePost"
                                value={values.enableWelcomePost}
                                onChange={(e) =>
                                  setFieldValue('enableWelcomePost', e.target.checked)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="divider" />
                    </div>
                    {/* Allow commnents Section */}
                    <div className="form-section">
                      <div className="form-section-columns">
                        <div className="left-section">
                          <h2 className="section-title">Allow Comments</h2>
                          <p className="section-description">
                            Let residents comment on posts in this group
                          </p>
                        </div>

                        <div className="right-section">
                          <div className="form-row">
                            <div className="form-field">
                              <ToggleField
                                name="allowComments"
                                value={values.allowComments}
                                onChange={(e) => setFieldValue('allowComments', e.target.checked)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="divider" />
                    </div>

                    {/* Approve commnents Section */}
                    <div className="form-section">
                      <div className="form-section-columns">
                        <div className="left-section">
                          <h2 className="section-title">Comment approval</h2>
                          <p className="section-description">
                            Require approval before resident comments appear
                          </p>
                        </div>

                        <div className="right-section">
                          <div className="form-row">
                            <div className="form-field">
                              <ToggleField
                                name="approveComments"
                                value={values.approveComments}
                                onChange={(e) => {
                                  const value = e.target.checked

                                  if (!value && groupData && groupHasPendingComments) {
                                    toastFactory.error(
                                      `Review pending comments for group ${groupData.name} before turning off Comment Approval.`,
                                    )
                                    return
                                  }

                                  setFieldValue('approveComments', e.target.checked)
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="form-actions">
                      {isEditMode && isGroupDeletable && (
                        <button
                          type="button"
                          className="button button-gray"
                          onClick={() => setDeleteGroup(groupData)}
                          disabled={loading}
                        >
                          <Trash size={20} />
                          Delete Group
                        </button>
                      )}

                      <button
                        type="button"
                        className="button button-primary"
                        disabled={isSubmitting || loading}
                        onClick={() =>
                          handleSubmit(
                            validateForm,
                            values,
                            setErrors,
                            setFieldTouched,
                            setSubmitting,
                          )
                        }
                      >
                        {isEditMode ? 'Save' : 'Create New Group'}
                      </button>
                    </div>
                  </form>
                )
              }}
            </Formik>
          </div>
          {isVisibleAllNeighbors && (
            <NeighborsWindow
              groupId={groupData.id}
              handleCancel={() => setVisibleAllNeighbors(false)}
            />
          )}
        </div>
      </Dashboard.Container>
      <ConfirmPopup
        isOpen={!!deleteGroup}
        cancelCallback={() => setDeleteGroup(null)}
        confirmCallback={handleDeleteGroup}
        cancelText="Cancel"
        confirmText="Delete"
        title="Confirmation"
        body="Are you sure you want to delete this group?"
      />
    </>
  )
}

export default GroupForm
